import { useQuery } from '@tanstack/react-query';

import { useGetClientCohorts } from 'api/generated/client-cohorts/client-cohorts';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { fetchMonitoringEnteringClasses } from 'containers/user/critical-elements-monitoring/mock/monitoring-mock-data';
import type { MonitoringFilterOptions } from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';

// TEMPORARY MOCK: entering classes still come from `fetchMonitoringEnteringClasses`
// until a real filter-options-style endpoint exists for them. Cohorts are real —
// sourced from `useGetClientCohorts` (`GET /api/{userId}/clients/{clientId}/cohorts`).
// Entering classes and cohorts rarely change, hence the long staleTime — this is
// intentionally not refetched on every filter change.
const FILTER_OPTIONS_STALE_TIME_MS = 10 * 60 * 1000;

export const monitoringEnteringClassesKeys = {
  all: ['monitoringEnteringClasses'] as const,
};

export interface UseMonitoringFilterOptionsResult {
  data: MonitoringFilterOptions | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

// `clientId` is passed down from the container (which already resolves it
// once and gates the whole page on it) rather than re-derived here from
// `useCurrentUser()` — matches the convention in the sibling hooks
// `use-monitoring-critical-elements.ts` / `use-monitoring-dashboard-metrics.ts`.
// Re-deriving it independently left `cohortsQuery` able to settle into a
// permanently-disabled state (TanStack Query v5 reports `isLoading`/`isError`
// as `false` for a disabled query) decoupled from the container's own
// `clientId` truthiness check, producing a silent infinite spinner.
export const useMonitoringFilterOptions = (
  clientId: string | undefined
): UseMonitoringFilterOptionsResult => {
  const currentUser = useCurrentUser();
  const userId = currentUser.data?.oktaUserId ?? '';

  const enteringClassesQuery = useQuery({
    queryKey: monitoringEnteringClassesKeys.all,
    queryFn: fetchMonitoringEnteringClasses,
    staleTime: FILTER_OPTIONS_STALE_TIME_MS,
  });

  const cohortsQuery = useGetClientCohorts(userId, Number(clientId), {
    query: {
      enabled: !!userId && !!clientId,
      staleTime: FILTER_OPTIONS_STALE_TIME_MS,
    },
  });

  // `ClientCohortDto.name` is both the display label and the filter value —
  // no client-side relabeling/templating (mirrors the backend's raw cohort
  // name pass-through). Drop any entry missing a name defensively.
  const cohorts = cohortsQuery.data
    ?.map((cohort) => cohort.name)
    .filter((name): name is string => !!name)
    .map((name) => ({ key: name, label: name }));

  const data: MonitoringFilterOptions | undefined =
    enteringClassesQuery.data && cohorts
      ? { enteringClasses: enteringClassesQuery.data, cohorts }
      : undefined;

  return {
    data,
    isLoading: enteringClassesQuery.isLoading || cohortsQuery.isLoading,
    isError: enteringClassesQuery.isError || cohortsQuery.isError,
    refetch: () => {
      enteringClassesQuery.refetch();
      cohortsQuery.refetch();
    },
  };
};

export default useMonitoringFilterOptions;
